import * as React from 'react'
  /* @jsx mdx */
import { mdx } from '@mdx-js/react';
/* @jsxRuntime classic */
/* @jsx mdx */
import * as IntegratePage from '../integrate/';
export const _frontmatter = {
  "title": "Mocking REST API",
  "order": 22
};
const makeShortcode = name => function MDXDefaultShortcode(props) {
  console.warn("Component " + name + " was not imported, exported, or provided by MDXProvider as global scope");
  return <div {...props} />;
};
const Action = makeShortcode("Action");
const PageLink = makeShortcode("PageLink");
const layoutProps = {
  _frontmatter
};
const MDXLayout = "wrapper";
export default function MDXContent({
  components,
  ...props
}) {
  return <MDXLayout {...layoutProps} {...props} components={components} mdxType="MDXLayout">
    <h2 {...{
      "id": "imports"
    }}>{`Imports`}</h2>
    <p>{`In our `}<inlineCode parentName="p">{`src/mocks/handlers.js`}</inlineCode>{` file let's import the essentials we need for mocking a REST API. They are grouped under the `}<inlineCode parentName="p">{`rest`}</inlineCode>{` namespace exposed by the library.`}</p>
    <Action mdxType="Action">
  Import <code>rest</code> from the <code>msw</code> package:
    </Action>
    <pre><code parentName="pre" {...{
        "className": "language-js",
        "metastring": "showLineNumbers",
        "showLineNumbers": true
      }}>{`// src/mocks/handlers.js
import { rest } from 'msw'
`}</code></pre>
    <h2 {...{
      "id": "request-handler"
    }}>{`Request handler`}</h2>
    <p>{`To handle a REST API request we need to specify its `}<em parentName="p">{`method`}</em>{`, `}<em parentName="p">{`path`}</em>{`, and a function that would return the mocked response.`}</p>
    <p>{`In this tutorial we will be mocking a basic login flow for our user. This flow implies handling two requests:`}</p>
    <ul>
      <li parentName="ul"><inlineCode parentName="li">{`POST /login`}</inlineCode>{`, to allow our user to log in;`}</li>
      <li parentName="ul"><inlineCode parentName="li">{`GET /user`}</inlineCode>{`, to return the information about the logged in user.`}</li>
    </ul>
    <Action mdxType="Action">
  Create request handlers by calling <code>rest[METHOD]</code> and providing a
  request path:
    </Action>
    <pre><code parentName="pre" {...{
        "className": "language-js",
        "metastring": "showLineNumbers focusedLines=6,9",
        "showLineNumbers": true,
        "focusedLines": "6,9"
      }}>{`// src/mocks/handlers.js
import { rest } from 'msw'

export const handlers = [
  // Handles a POST /login request
  rest.post('/login', null),

  // Handles a GET /user request
  rest.get('/user', null),
]
`}</code></pre>
    <h2 {...{
      "id": "response-resolver"
    }}>{`Response resolver`}</h2>
    <p>{`To respond to an intercepted request we have to specify a mocked response using a response resolver function.`}</p>
    <p>{`Response resolver is a function that accepts the following arguments:`}</p>
    <ul>
      <li parentName="ul"><inlineCode parentName="li">{`req`}</inlineCode>{`, an information about a matching request;`}</li>
      <li parentName="ul"><inlineCode parentName="li">{`res`}</inlineCode>{`, a functional utility to create the mocked response;`}</li>
      <li parentName="ul"><inlineCode parentName="li">{`ctx`}</inlineCode>{`, a group of functions that help to set a status code, headers, body, etc. of the mocked response.`}</li>
    </ul>
    <Action mdxType="Action">
  Provide response resolvers to the previously defined request handlers:
    </Action>
    <pre><code parentName="pre" {...{
        "className": "language-js",
        "metastring": "showLineNumbers focusedLines=9-12,21-25,30-35",
        "showLineNumbers": true,
        "focusedLines": "9-12,21-25,30-35"
      }}>{`// src/mocks/handlers.js
import { rest } from 'msw'

export const handlers = [
  rest.post('/login', (req, res, ctx) => {
    // Persist user's authentication in the session
    sessionStorage.setItem('is-authenticated', 'true')

    return res(
      // Respond with a 200 status code
      ctx.status(200),
    )
  }),

  rest.get('/user', (req, res, ctx) => {
    // Check if the user is authenticated in this session
    const isAuthenticated = sessionStorage.getItem('is-authenticated')

    if (!isAuthenticated) {
      // If not authenticated, respond with a 403 error
      return res(
        ctx.status(403),
        ctx.json({
          errorMessage: 'Not authorized',
        }),
      )
    }

    // If authenticated, return a mocked user details
    return res(
      ctx.status(200),
      ctx.json({
        username: 'admin',
      }),
    )
  }),
]
`}</code></pre>
    <blockquote>
      <p parentName="blockquote">{`Utilize things like `}<a parentName="p" {...{
          "href": "https://developer.mozilla.org/en-US/docs/Web/API/Window/sessionStorage"
        }}><inlineCode parentName="a">{`sessionStorage`}</inlineCode></a>{`, `}<a parentName="p" {...{
          "href": "https://developer.mozilla.org/en-US/docs/Web/API/Window/localStorage"
        }}><inlineCode parentName="a">{`localStorage`}</inlineCode></a>{`, or `}<a parentName="p" {...{
          "href": "https://developer.mozilla.org/en-US/docs/Web/API/IndexedDB_API"
        }}>{`IndexedDB`}</a>{` to handle more complex API scenarios and user interactions.`}</p>
    </blockquote>
    <h2 {...{
      "id": "next-step"
    }}>{`Next step`}</h2>
    <p>{`The request handlers are complete, yet there is one last step to perform: integrate the mocking.`}</p>

    <PageLink page={IntegratePage} url="/docs/getting-started/integrate" mdxType="PageLink" />

    </MDXLayout>;
}
;
MDXContent.isMDXComponent = true;
      